<script setup>
/* global axios */
// Use the globally-configured axios (carries the agent's auth header), exactly
// like ConversationHeader does — NOT the raw `axios` package, which would send
// unauthenticated requests and get a 401.
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  accountId: { type: [Number, String], required: true },
  contactId: { type: [Number, String], required: true },
  conversationId: { type: [Number, String], required: true },
  // The shipment pill object from ConversationHeader (has row_name + shipping_number).
  shipment: { type: Object, default: () => ({}) },
  // The conversation's 24h reply window — controls whether we can send to the customer.
  canReply: { type: Boolean, default: true },
});

const emit = defineEmits(['update:show']);

const { t } = useI18n();
const store = useStore();

const detail = ref(null);
const isLoading = ref(false);
const loadError = ref(false);

// 'timeline' (default) ↔ 'preview' (rendered card awaiting confirm-send)
const mode = ref('timeline');
const preview = ref(null);
const isRendering = ref(false);
const isSending = ref(false);

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const rowName = computed(() => props.shipment?.row_name);
const code = computed(
  () => props.shipment?.shipping_number || detail.value?.shipping_number
);

const STEP_ORDER = [
  'التسجيل',
  'الشحن',
  'النقل والمعالجة',
  'التسليم',
  'اكتمال الطلب',
];

// Normalise the fixed 5-step ERP timeline into render-ready nodes.
const steps = computed(() => {
  const byStep = {};
  (detail.value?.timeline || []).forEach(s => {
    byStep[s.step] = s.details || [];
  });
  return STEP_ORDER.map(label => {
    const d = byStep[label] || [];
    return {
      label,
      done: d.length > 0,
      event: d[0] || '',
      office: d[1] || '',
      gov: d[2] || '',
      date: d[3] || '',
      time: d[4] || '',
    };
  });
});

const statusText = computed(
  () =>
    detail.value?.status_ar ||
    detail.value?.status ||
    props.shipment?.status_display ||
    ''
);

const fetchDetail = async () => {
  if (!rowName.value) return;
  isLoading.value = true;
  loadError.value = false;
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${props.accountId}/contacts/${props.contactId}/shipment_detail`,
      { params: { row_name: rowName.value } }
    );
    detail.value = data;
  } catch (error) {
    loadError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const openPreview = async () => {
  if (isRendering.value) return;
  isRendering.value = true;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${props.accountId}/contacts/${props.contactId}/tracking_preview`,
      { row_name: rowName.value }
    );
    preview.value = data;
    mode.value = 'preview';
  } catch (error) {
    useAlert(t('CONVERSATION.HEADER.TRACKING.RENDER_ERROR'));
  } finally {
    isRendering.value = false;
  }
};

const base64ToFile = (b64, filename) => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], filename, { type: 'image/png' });
};

const confirmSend = async () => {
  if (isSending.value || !preview.value) return;
  isSending.value = true;
  try {
    const file = base64ToFile(
      preview.value.image_base64,
      `tracking-${preview.value.code || 'shipment'}.png`
    );
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: props.conversationId,
      message: preview.value.caption,
      files: [file],
      // Must be an explicit boolean: a missing `private` reaches the multipart
      // payload as the string "undefined", which Rails casts to TRUE — sending
      // the tracking as a private note instead of a real reply to the customer.
      private: false,
    });
    useAlert(t('CONVERSATION.HEADER.TRACKING.SENT_SUCCESS'));
    emit('update:show', false);
  } catch (error) {
    useAlert(t('CONVERSATION.HEADER.TRACKING.SEND_ERROR'));
  } finally {
    isSending.value = false;
  }
};

const onClose = () => {
  emit('update:show', false);
};

// Reset to a clean state and (re)load whenever the modal is opened.
watch(
  () => props.show,
  isOpen => {
    if (isOpen) {
      mode.value = 'timeline';
      preview.value = null;
      fetchDetail();
    }
  },
  { immediate: true }
);
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="t('CONVERSATION.HEADER.TRACKING.TITLE')"
        :header-content="code"
      />

      <!-- TIMELINE -->
      <div v-if="mode === 'timeline'" class="px-8 pb-8">
        <div v-if="isLoading" class="py-10 text-center text-n-slate-11">
          <span
            class="i-lucide-loader-circle size-6 animate-spin inline-block"
          />
        </div>

        <div v-else-if="loadError" class="py-8 text-center text-n-ruby-11">
          {{ t('CONVERSATION.HEADER.TRACKING.LOAD_ERROR') }}
        </div>

        <template v-else>
          <div class="flex items-center justify-between mb-5">
            <span
              class="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-n-teal-3 text-n-teal-11"
            >
              {{ statusText }}
            </span>
            <span
              v-if="detail?.customer_name"
              class="text-sm font-medium text-n-slate-11"
            >
              {{ detail.customer_name }}
            </span>
          </div>

          <div class="flex flex-col">
            <div
              v-for="(step, index) in steps"
              :key="step.label"
              class="flex gap-3"
            >
              <!-- rail: node + connector -->
              <div class="flex flex-col items-center flex-shrink-0 w-6">
                <span
                  class="flex items-center justify-center rounded-full size-6 text-[11px] font-bold"
                  :class="
                    step.done
                      ? 'bg-n-teal-9 text-white'
                      : 'bg-n-alpha-2 text-n-slate-10'
                  "
                >
                  <span v-if="step.done" class="i-lucide-check size-3.5" />
                  <span v-else class="size-1.5 rounded-full bg-n-slate-8" />
                </span>
                <span
                  v-if="index < steps.length - 1"
                  class="w-0.5 flex-1 min-h-6 my-1 rounded"
                  :class="step.done ? 'bg-n-teal-9' : 'bg-n-alpha-2'"
                />
              </div>
              <!-- content -->
              <div class="pb-5 min-w-0">
                <p
                  class="text-sm font-bold leading-tight"
                  :class="step.done ? 'text-n-slate-12' : 'text-n-slate-10'"
                >
                  {{ step.label }}
                </p>
                <template v-if="step.done">
                  <p v-if="step.event" class="mt-1 text-xs text-n-slate-11">
                    {{ step.event }}
                  </p>
                  <p
                    v-if="step.office || step.gov"
                    class="mt-0.5 text-xs text-n-slate-10"
                  >
                    {{ [step.office, step.gov].filter(Boolean).join(' — ') }}
                  </p>
                  <p
                    v-if="step.date || step.time"
                    class="mt-0.5 text-[11px] text-n-slate-10"
                  >
                    {{ [step.date, step.time].filter(Boolean).join(' ') }}
                  </p>
                </template>
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-2 pt-4 mt-2 border-t border-n-weak">
            <p v-if="!canReply" class="text-xs text-n-amber-11 text-center">
              {{ t('CONVERSATION.HEADER.TRACKING.WINDOW_CLOSED') }}
            </p>
            <div class="flex justify-end gap-2">
              <NextButton
                faded
                slate
                :label="t('CONVERSATION.HEADER.TRACKING.CLOSE')"
                @click="onClose"
              />
              <NextButton
                :label="t('CONVERSATION.HEADER.TRACKING.SEND_TO_CUSTOMER')"
                :is-loading="isRendering"
                :disabled="!canReply || isRendering"
                icon="i-lucide-send"
                @click="openPreview"
              />
            </div>
          </div>
        </template>
      </div>

      <!-- PREVIEW + CONFIRM -->
      <div v-else class="px-8 pb-8">
        <p class="mb-3 text-sm text-n-slate-11">
          {{ t('CONVERSATION.HEADER.TRACKING.PREVIEW_HINT') }}
        </p>
        <div class="p-2 border rounded-lg border-n-weak bg-n-alpha-1">
          <img
            :src="`data:image/png;base64,${preview.image_base64}`"
            :alt="t('CONVERSATION.HEADER.TRACKING.TITLE')"
            class="w-full rounded"
          />
        </div>
        <p
          class="mt-3 p-3 text-xs whitespace-pre-line rounded-lg bg-n-alpha-1 text-n-slate-11"
          dir="auto"
        >
          {{ preview.caption }}
        </p>

        <div
          class="flex justify-between gap-2 pt-4 mt-2 border-t border-n-weak"
        >
          <NextButton
            faded
            slate
            icon="i-lucide-arrow-right"
            :label="t('CONVERSATION.HEADER.TRACKING.BACK')"
            :disabled="isSending"
            @click="mode = 'timeline'"
          />
          <NextButton
            teal
            icon="i-lucide-send"
            :label="t('CONVERSATION.HEADER.TRACKING.CONFIRM_SEND')"
            :is-loading="isSending"
            :disabled="isSending"
            @click="confirmSend"
          />
        </div>
      </div>
    </div>
  </woot-modal>
</template>
